import { SAMPLE_AVS_DEG, SAMPLE_GZ_CURVE } from "@nav-games/safety";

/// GZ curve diagram — righting arm GZ (m) vs heel angle (°) for a
/// representative 40 ft cruising yacht. Shows the positive (righting)
/// lobe up to AVS, the zero crossing at AVS, and the negative
/// (capsizing) lobe beyond.

interface Props {
  size?: number;
}

const VW = 480;
const VH = 280;
const PAD_L = 50;
const PAD_R = 12;
const PAD_T = 20;
const PAD_B = 36;
const PLOT_W = VW - PAD_L - PAD_R;
const PLOT_H = VH - PAD_T - PAD_B;

const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const POS = "#2c7a3b";
const NEG = "#c0382b";

export function GzCurveFigure({ size = VW }: Props) {
  const minGz = Math.min(...SAMPLE_GZ_CURVE.map((p) => p.gzMetres));
  const maxGz = Math.max(...SAMPLE_GZ_CURVE.map((p) => p.gzMetres));
  const yScale = PLOT_H / (maxGz - minGz);
  const xScale = PLOT_W / 180;

  const x = (deg: number): number => PAD_L + deg * xScale;
  const y = (gz: number): number =>
    PAD_T + (maxGz - gz) * yScale;

  // Polygon paths for the positive + negative regions.
  const positivePts: string[] = [];
  const negativePts: string[] = [];
  for (const p of SAMPLE_GZ_CURVE) {
    if (p.gzMetres >= 0) positivePts.push(`${x(p.heelDeg)},${y(p.gzMetres)}`);
    else negativePts.push(`${x(p.heelDeg)},${y(p.gzMetres)}`);
  }
  // Close off region polygons at the zero line.
  const yZero = y(0);
  const positivePoly = [
    `${x(0)},${yZero}`,
    ...positivePts,
    `${x(SAMPLE_AVS_DEG)},${yZero}`,
  ].join(" ");
  const negativePoly = [
    `${x(SAMPLE_AVS_DEG)},${yZero}`,
    ...negativePts,
    `${x(180)},${yZero}`,
  ].join(" ");

  const linePath = SAMPLE_GZ_CURVE.map((p, i) =>
    `${i === 0 ? "M" : "L"} ${x(p.heelDeg)} ${y(p.gzMetres)}`,
  ).join(" ");

  // Y-axis ticks at every 0.1 m.
  const yTicks: number[] = [];
  for (let g = Math.ceil(minGz * 10) / 10; g <= maxGz + 0.001; g += 0.1) {
    yTicks.push(Math.round(g * 10) / 10);
  }

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="GZ righting-arm curve"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Background. */}
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
      />

      {/* Filled regions. */}
      <polygon points={positivePoly} fill={`${POS}33`} />
      <polygon points={negativePoly} fill={`${NEG}33`} />

      {/* Y-axis. */}
      <line
        x1={PAD_L}
        y1={PAD_T}
        x2={PAD_L}
        y2={PAD_T + PLOT_H}
        stroke={STROKE}
        strokeWidth={1.5}
      />
      {yTicks.map((g) => (
        <g key={g}>
          <line
            x1={PAD_L - 4}
            y1={y(g)}
            x2={PAD_L}
            y2={y(g)}
            stroke={STROKE}
            strokeWidth={1}
          />
          <text
            x={PAD_L - 6}
            y={y(g) + 3}
            fontSize={9}
            textAnchor="end"
            fill={MUTED}
            fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          >
            {g.toFixed(1)}
          </text>
        </g>
      ))}
      <text
        x={14}
        y={PAD_T + PLOT_H / 2}
        fontSize={11}
        textAnchor="middle"
        fill={STROKE}
        fontWeight={600}
        transform={`rotate(-90 14 ${PAD_T + PLOT_H / 2})`}
      >
        Righting arm GZ (m)
      </text>

      {/* X-axis at zero. */}
      <line
        x1={PAD_L}
        y1={yZero}
        x2={PAD_L + PLOT_W}
        y2={yZero}
        stroke={STROKE}
        strokeWidth={1.5}
      />
      {[0, 30, 60, 90, 120, 150, 180].map((deg) => (
        <g key={deg}>
          <line
            x1={x(deg)}
            y1={yZero}
            x2={x(deg)}
            y2={yZero + 4}
            stroke={STROKE}
            strokeWidth={1}
          />
          <text
            x={x(deg)}
            y={yZero + 16}
            fontSize={9}
            textAnchor="middle"
            fill={MUTED}
          >
            {deg}°
          </text>
        </g>
      ))}
      <text
        x={PAD_L + PLOT_W / 2}
        y={VH - 6}
        fontSize={11}
        textAnchor="middle"
        fill={STROKE}
        fontWeight={600}
      >
        Heel angle (°)
      </text>

      {/* GZ curve line. */}
      <path d={linePath} fill="none" stroke={STROKE} strokeWidth={2} />

      {/* AVS marker. */}
      <line
        x1={x(SAMPLE_AVS_DEG)}
        y1={PAD_T}
        x2={x(SAMPLE_AVS_DEG)}
        y2={PAD_T + PLOT_H}
        stroke={NEG}
        strokeWidth={1}
        strokeDasharray="3 3"
      />
      <text
        x={x(SAMPLE_AVS_DEG)}
        y={PAD_T + 12}
        fontSize={10}
        textAnchor="middle"
        fill={NEG}
        fontWeight={700}
      >
        AVS {SAMPLE_AVS_DEG}°
      </text>

      {/* Region labels. */}
      <text
        x={x(45)}
        y={y(0.4)}
        fontSize={10}
        textAnchor="middle"
        fill={POS}
        fontWeight={700}
      >
        SELF-RIGHTING
      </text>
      <text
        x={x(155)}
        y={y(-0.1)}
        fontSize={10}
        textAnchor="middle"
        fill={NEG}
        fontWeight={700}
      >
        CAPSIZING
      </text>
    </svg>
  );
}
